import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/**
 * Every detail a TPA form can ask for, in one table so the id list, the
 * default labels, and the sensitivity set cannot drift apart.
 *
 * `sensitive` marks the identifiers that never leave the reimbursement service
 * toward a client. Cleared with Security. Saved accounts can reuse them but if
 * the customer makes an edit, they must supply the values for these fields.
 */
const HSA_REIMBURSEMENT_FIELDS = [
  { id: "firstName", label: "First name", sensitive: false },
  { id: "lastName", label: "Last name", sensitive: false },
  { id: "middleInitial", label: "Middle initial", sensitive: false },
  { id: "suffix", label: "Suffix", sensitive: false },
  { id: "gender", label: "Gender", sensitive: false },
  { id: "dob", label: "DOB", sensitive: true },
  { id: "street", label: "Street address", sensitive: false },
  { id: "city", label: "City", sensitive: false },
  { id: "state", label: "State", sensitive: false },
  { id: "zip", label: "Zip", sensitive: false },
  { id: "phone", label: "Phone", sensitive: false },
  { id: "email", label: "Email", sensitive: false },
  { id: "employerName", label: "Employer name", sensitive: false },
  { id: "employerId", label: "Employer ID", sensitive: false },
  { id: "employerLocation", label: "Employer location", sensitive: false },
  { id: "department", label: "Department", sensitive: false },
  { id: "ssn", label: "SSN", sensitive: true },
  { id: "memberId", label: "Member ID", sensitive: true },
  { id: "accountNumber", label: "Account number", sensitive: true },
  { id: "groupNumber", label: "Group number", sensitive: true },
  { id: "planId", label: "Plan ID", sensitive: true },
  { id: "fsaControlNumber", label: "FSA control number", sensitive: true },
  { id: "planYearStartDate", label: "Plan year start date", sensitive: false },
] as const satisfies readonly {
  id: string;
  label: string;
  sensitive: boolean;
}[];

export type HsaReimbursementFieldId =
  (typeof HSA_REIMBURSEMENT_FIELDS)[number]["id"];

export const HSA_REIMBURSEMENT_FIELD_IDS: readonly HsaReimbursementFieldId[] =
  HSA_REIMBURSEMENT_FIELDS.map((field) => field.id);

export const SENSITIVE_HSA_REIMBURSEMENT_FIELD_IDS: readonly HsaReimbursementFieldId[] =
  HSA_REIMBURSEMENT_FIELDS.filter((field) => field.sensitive).map(
    (field) => field.id,
  );

const SENSITIVE_FIELD_ID_SET = new Set<HsaReimbursementFieldId>(
  SENSITIVE_HSA_REIMBURSEMENT_FIELD_IDS,
);

export function isSensitiveHsaReimbursementField(
  id: HsaReimbursementFieldId,
): boolean {
  return SENSITIVE_FIELD_ID_SET.has(id);
}

export const hsaReimbursementFieldId = createEnum(HSA_REIMBURSEMENT_FIELD_IDS);

export type HsaReimbursementSsnCollect = "full" | "last4";

export const hsaReimbursementSsnCollect = z.enum(["full", "last4"]);

const DEFAULT_FIELD_LABELS = new Map<HsaReimbursementFieldId, string>(
  HSA_REIMBURSEMENT_FIELDS.map((field) => [field.id, field.label]),
);

export function defaultFieldLabel(
  id: HsaReimbursementFieldId,
  collect?: HsaReimbursementSsnCollect,
): string {
  if (id === "ssn" && collect === "last4") {
    return "Last 4 digits of SSN";
  }
  return DEFAULT_FIELD_LABELS.get(id) ?? id;
}

export type HsaReimbursementProviderField =
  | { id: "ssn"; label?: string; collect: HsaReimbursementSsnCollect }
  | {
      id: Exclude<HsaReimbursementFieldId, "ssn">;
      label?: string;
      collect?: undefined;
    };

export interface HsaReimbursementProviderForm {
  /**
   * The administrator's confirmed claims fax line, E.164. Required: a form
   * Redo cannot deliver is not a form Redo supports, so the directory drops
   * any form whose number is not valid.
   */
  faxNumber: string;
  fields: HsaReimbursementProviderField[];
}

export interface HsaReimbursementProviderDirectoryEntry {
  hsa: HsaReimbursementProviderForm | null;
  fsa: HsaReimbursementProviderForm | null;
}

export const hsaReimbursementFieldSpec = z.object({
  id: hsaReimbursementFieldId,
  label: z.string(),
  collect: hsaReimbursementSsnCollect.optional(),
});

export type HsaReimbursementFieldSpec = z.infer<
  typeof hsaReimbursementFieldSpec
>;

export function resolveCatalogField(
  field: HsaReimbursementProviderField,
): HsaReimbursementFieldSpec {
  return {
    id: field.id,
    label: field.label ?? defaultFieldLabel(field.id, field.collect),
    collect: field.id === "ssn" ? field.collect : undefined,
  };
}

const hsaReimbursementDetailsShape: Record<
  HsaReimbursementFieldId,
  z.ZodOptional<z.ZodString>
> = {
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  middleInitial: z.string().optional(),
  suffix: z.string().optional(),
  gender: z.string().optional(),
  dob: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  employerName: z.string().optional(),
  employerId: z.string().optional(),
  employerLocation: z.string().optional(),
  department: z.string().optional(),
  ssn: z.string().optional(),
  memberId: z.string().optional(),
  accountNumber: z.string().optional(),
  groupNumber: z.string().optional(),
  planId: z.string().optional(),
  fsaControlNumber: z.string().optional(),
  planYearStartDate: z.string().optional(),
};

export const hsaReimbursementDetails = z
  .object(hsaReimbursementDetailsShape)
  .strict();

export type HsaReimbursementDetails = z.infer<typeof hsaReimbursementDetails>;

const FIELD_ID_SET = new Set<string>(HSA_REIMBURSEMENT_FIELD_IDS);

export function isHsaReimbursementFieldId(
  value: string,
): value is HsaReimbursementFieldId {
  return FIELD_ID_SET.has(value);
}

export const DERIVED_DETAIL_SOURCES = [
  "ssnLast4",
  "ssnDigit.0",
  "ssnDigit.1",
  "ssnDigit.2",
  "ssnDigit.3",
  "ssnDigit.4",
  "ssnDigit.5",
  "ssnDigit.6",
  "ssnDigit.7",
  "ssnDigit.8",
  "dobMM",
  "dobDD",
  "dobYY",
  "dobYYYY",
  "dobMMDDYYYY",
] as const;

export type DerivedDetailSource = (typeof DERIVED_DETAIL_SOURCES)[number];

const DERIVED_DETAIL_SOURCE_SET = new Set<string>(DERIVED_DETAIL_SOURCES);

export function isDerivedDetailSource(
  value: string,
): value is DerivedDetailSource {
  return DERIVED_DETAIL_SOURCE_SET.has(value);
}

export function detailValue(
  details: HsaReimbursementDetails,
  id: HsaReimbursementFieldId,
): string {
  return details[id] ?? "";
}
